import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Button, Input, Badge, PageTemplate, Alert, Modal } from '../components/ui';
import TimeSlotCalendar from '../components/TimeSlotCalendar';
import { Html5QrcodeScanner } from 'html5-qrcode';

import { FiCalendar, FiClock, FiTool, FiX, FiCheck, FiAlertCircle, FiCamera, FiCheckCircle, FiSearch, FiChevronDown, FiMapPin } from 'react-icons/fi';

const QrScannerComponent = ({ onScan, onError, active }) => {
    const scannerRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        // Clear any existing scanner first
        if (scannerRef.current) {
            scannerRef.current.clear().catch(() => {});
            scannerRef.current = null;
        }

        if (!active) {
            return;
        }

        // Small delay to ensure DOM is ready and previous scanner is fully cleaned up
        const timeoutId = setTimeout(() => {
            // Clear the container contents to remove any leftover UI
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }

            const config = {
                fps: 10,
                qrbox: { width: 250, height: 250 },
            };

            const scanner = new Html5QrcodeScanner('equipment-qr-reader', config, false);
            scannerRef.current = scanner;

            scanner.render(
                (decodedText /*, decodedResult */) => {
                    if (onScan) onScan(decodedText);
                },
                (errorMessage) => {
                    // html5-qrcode is very noisy; only forward serious errors
                    if (onError && typeof errorMessage === 'string' && !errorMessage.includes('QR code parse error')) {
                        onError(new Error(errorMessage));
                    }
                }
            );
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (scannerRef.current) {
                scannerRef.current.clear().catch(() => {});
                scannerRef.current = null;
            }
            // Also clear the container on unmount
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
        };
    }, [active, onScan, onError]);

    if (!active) return null;

    return (
        <div
            ref={containerRef}
            id="equipment-qr-reader"
            className="rounded-lg overflow-hidden border border-primary-300 dark:border-gray-600"
        />
    );
};


function EquipmentBooking() {

    const [equipment, setEquipment] = useState([]);
    const [groupedEquipment, setGroupedEquipment] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState('');
    const [selectedEquipmentData, setSelectedEquipmentData] = useState(null);
    const [equipmentSearch, setEquipmentSearch] = useState('');
    
    // Set default date to today in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    // Set default time to current time in HH:MM format
    const getCurrentTime = () => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };
    
    const [bookingDate, setBookingDate] = useState(getTodayDate());
    const [bookingTime, setBookingTime] = useState(getCurrentTime());
    const [bookingDuration, setBookingDuration] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [calendarSlotKeys, setCalendarSlotKeys] = useState([]);
    const [calendarSelectionValid, setCalendarSelectionValid] = useState(false);

    const maxEquipmentBookableDate = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 84);
        return d;
    }, []);

    // Extended booking form fields
    const [quantity, setQuantity] = useState(1);
    const [affiliation, setAffiliation] = useState('');
    const [remarks, setRemarks] = useState('');
    const [countryCode, setCountryCode] = useState('+852');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [courseCode, setCourseCode] = useState('');
    const [accountCode, setAccountCode] = useState('');
    const [bookingNature, setBookingNature] = useState('');
    const [acceptedEquipmentLoanPolicy, setAcceptedEquipmentLoanPolicy] = useState(false);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    
    // QR Scanner State
    const [showScanner, setShowScanner] = useState(false);
    const [scanSuccess, setScanSuccess] = useState(false);
    const [scanMessage, setScanMessage] = useState('');

    const handleAuthExpired = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setError('Your session expired. Please sign in again.');
        window.location.href = '/login';
    };
    
    // Weekend validation function
    const isWeekend = (dateString) => {
        const date = new Date(dateString + 'T00:00:00');
        const day = date.getDay();
        return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
    };
    
    const handleCalendarSlotsChange = (keys) => {
        setCalendarSlotKeys(keys);
        if (!keys.length) {
            setCalendarSelectionValid(false);
            setError('');
            return;
        }
        const parts = keys.map((k) => {
            const [d, t] = k.split('|');
            return { d, t, hour: parseInt(t.split(':')[0], 10) };
        }).sort((a, b) => a.d.localeCompare(b.d) || a.hour - b.hour);

        const dateStr = parts[0].d;
        if (!parts.every((p) => p.d === dateStr)) {
            setError('Select time slots on a single weekday only.');
            setCalendarSelectionValid(false);
            return;
        }
        if (isWeekend(dateStr)) {
            setError('Equipment cannot be borrowed on weekends.');
            setCalendarSelectionValid(false);
            return;
        }
        const selDay = new Date(`${dateStr}T12:00:00`);
        selDay.setHours(0, 0, 0, 0);
        if (selDay > maxEquipmentBookableDate) {
            setError('Equipment cannot be booked more than 12 weeks in advance.');
            setCalendarSelectionValid(false);
            return;
        }
        const hours = [...new Set(parts.map((p) => p.hour))].sort((a, b) => a - b);
        const minH = hours[0];
        const contiguous = hours.every((h, i) => h === minH + i);
        if (!contiguous) {
            setError('Select consecutive hour slots on the same day.');
            setCalendarSelectionValid(false);
            return;
        }
        setError('');
        setCalendarSelectionValid(true);
        setBookingDate(dateStr);
        setBookingTime(`${String(minH).padStart(2, '0')}:00`);
        setBookingDuration(hours.length);
    };
    
    // Group equipment by location
    const groupEquipmentByLocation = (equipmentList) => {
        const grouped = {};
        equipmentList.forEach(item => {
            const location = item.location || 'Unknown Location';
            if (!grouped[location]) {
                grouped[location] = [];
            }
            grouped[location].push(item);
        });
        return grouped;
    };

    useEffect(() => {
        fetchEquipment();
        fetchGroupedEquipment();
        fetchUserBookings();
    }, []);

    useEffect(() => {
        setCalendarSlotKeys([]);
        setCalendarSelectionValid(false);
    }, [selectedEquipment]);

    const fetchGroupedEquipment = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthExpired();
                return;
            }
            
            const response = await fetch('/api/equipment/grouped', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('Grouped equipment fetched:', data.length, 'types');
                setGroupedEquipment(data || []);
            } else {
                if (response.status === 401) {
                    handleAuthExpired();
                    return;
                }
                console.error('Failed to fetch grouped equipment');
                setGroupedEquipment([]);
            }
        } catch (error) {
            console.error('Error fetching grouped equipment:', error);
            setGroupedEquipment([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchEquipment = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthExpired();
                return;
            }
            console.log('Fetching equipment with token:', token ? 'Token exists' : 'No token');
            
            // Add timeout to prevent hanging (increased for large datasets)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for large equipment lists
            
            let response;
            try {
                response = await fetch('/api/equipment', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    console.error('Equipment fetch timeout after 30 seconds - response may be too large');
                    setEquipment([]);
                    setLoading(false);
                    return;
                }
                throw fetchError;
            }
            
            console.log('Equipment API response status:', response.status);
            if (response.ok) {
                const data = await response.json();
                console.log('Equipment fetched:', data.length, 'items');
                // Show all equipment (not just available) - users can see what exists
                // Availability will be shown in the dropdown
                setEquipment(data || []);
                if (data.length === 0) {
                    console.warn('⚠️ No equipment found. Check if MongoDB is connected or equipment exists in database.');
                }
            } else {
                if (response.status === 401) {
                    handleAuthExpired();
                    return;
                }
                const errorData = await response.json().catch(() => ({}));
                console.error('Equipment API error:', response.status, errorData);
                
                // Check if it's a MongoDB connection error (503)
                if (response.status === 503) {
                    console.error('❌ Database connection failed:', errorData.message || 'Database unavailable');
                    setEquipment([]);
                } else {
                    setEquipment([]); // Set empty array on error
                }
            }
        } catch (error) {
            console.error('Error fetching equipment:', error);
            setEquipment([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    const fetchUserBookings = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthExpired();
                return;
            }
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            
            let response;
            try {
                response = await fetch('/api/equipment-bookings/user', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    console.error('Bookings fetch timeout');
                    setBookings([]);
                    return;
                }
                throw fetchError;
            }
            
            if (response.ok) {
                const data = await response.json();
                setBookings(data || []);
            } else {
                if (response.status === 401) {
                    handleAuthExpired();
                    return;
                }
                setBookings([]);
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setBookings([]);
        }
    };
    
    // Helper function to validate MongoDB ObjectId format (24 hex characters)
    const isValidObjectId = (str) => {
        return /^[a-fA-F0-9]{24}$/.test(str);
    };

    // NEW FUNCTION: Handles the result from the QR code scanner
    const handleScan = async (result) => {
        try {
            if (!result) return;

            // Normalise result into a string, because some browsers/libs
            // may pass an object or array instead of a plain string
            let rawText = '';

            if (typeof result === 'string') {
                rawText = result;
            } else if (Array.isArray(result)) {
                // Try common shapes from barcode APIs
                const first = result[0] || {};
                rawText = first.rawValue || first.text || JSON.stringify(first);
            } else if (typeof result === 'object') {
                rawText = result.text || result.rawValue || JSON.stringify(result);
            } else {
                console.warn('QR scan result in unexpected format:', result);
                return;
            }

            const trimmedResult = rawText.trim();

            let equipmentId = null;

            // Try to extract the equipment ID from the QR code
            // Support multiple formats:
            // 1. Plain MongoDB ObjectId string (24 hex characters)
            // 2. JSON with _id field
            // 3. MongoDB Extended JSON with $oid

            // Check if it's a plain ObjectId string
            if (isValidObjectId(trimmedResult)) {
                equipmentId = trimmedResult;
            } else {
                // Try parsing as JSON
                try {
                    const data = JSON.parse(trimmedResult);
                    equipmentId = data._id?.$oid || data._id || data.id || data.equipmentId;
                } catch (error) {
                    // Not valid JSON - check if it might contain an ObjectId somewhere
                    const objectIdMatch = trimmedResult.match(/[a-fA-F0-9]{24}/);
                    if (objectIdMatch) {
                        equipmentId = objectIdMatch[0];
                    }
                }
            }

            if (!equipmentId || !isValidObjectId(equipmentId)) {
                setScanSuccess(false);
                setScanMessage('Invalid QR code format. Expected a valid equipment ID.');
                setTimeout(() => setScanMessage(''), 3000);
                return;
            }

            // Find the equipment in the local state
            let foundEquipment = equipment.find(item => String(item._id) === String(equipmentId));

            // Fallback: ask backend directly in case local list is stale or filtered
            if (!foundEquipment) {
                try {
                    const response = await fetch(`/api/equipment/${equipmentId}`, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    });
                    if (response.ok) {
                        const eq = await response.json();
                        foundEquipment = eq;
                        // Optionally merge into local equipment list for display
                        setEquipment(prev => {
                            const exists = prev.some(e => String(e._id) === String(eq._id));
                            return exists ? prev : [...prev, eq];
                        });
                    }
                } catch (fetchErr) {
                    console.error('Error fetching equipment by ID after scan:', fetchErr);
                }
            }

            if (foundEquipment) {
                const gid = String(foundEquipment._id);
                const fromGroup = groupedEquipment.find((g) =>
                    (g.available_ids || []).some((id) => String(id) === gid)
                );
                const data =
                    fromGroup || {
                        equipment_name: foundEquipment.eqm_name,
                        equipment_category: foundEquipment.eqm_cat,
                        equipment_type: foundEquipment.eqm_type,
                        available_count: 1,
                        available_ids: [foundEquipment._id],
                        location: foundEquipment.location || ''
                    };
                const groupKey = `${data.equipment_name}-${data.equipment_category}-${data.equipment_type}`;
                setSelectedEquipment(groupKey);
                setSelectedEquipmentData(data);
                setShowScanner(false); // Close the scanner after a successful scan
                setScanSuccess(true);
                setScanMessage(`Equipment Selected: ${foundEquipment.eqm_name}`);
                // Clear message after 5 seconds
                setTimeout(() => {
                    setScanMessage('');
                    setScanSuccess(false);
                }, 5000);
            } else {
                // Equipment not found locally or on backend
                setScanSuccess(false);
                setScanMessage(`Equipment not found for ID: ${equipmentId}. It may not be available or does not exist.`);
                setTimeout(() => setScanMessage(''), 4000);
            }
        } catch (err) {
            console.error('Error handling QR scan result:', err, 'raw result:', result);
            setScanSuccess(false);
            setScanMessage('Unexpected error while reading QR code.');
            setTimeout(() => setScanMessage(''), 3000);
        }
    };
    
    // NEW FUNCTION: Handles scanner errors (e.g., camera access denied)
    const handleError = (err) => {
        console.error('QR Scanner Error:', err);
        // Optionally, inform the user if the camera cannot be accessed
        if (err.name === 'NotAllowedError') {
             alert('Camera access denied. Please manually select equipment.');
        } else if (err.name === 'NotFoundError') {
             // For devices without a camera
             alert('No camera detected. Please manually select equipment.');
        }
        setShowScanner(false); // Fallback to dropdown on major error
    };


    const handleSubmitBooking = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthExpired();
                return;
            }
            if (!calendarSelectionValid || calendarSlotKeys.length === 0) {
                alert('Please select consecutive available hour slots on the availability calendar.');
                setSubmitting(false);
                return;
            }

            // Validate weekend dates before submission
            if (isWeekend(bookingDate)) {
                alert('Equipment cannot be borrowed on weekends (Saturday and Sunday). Please select a weekday.');
                setSubmitting(false);
                return;
            }

            const bookingDayCheck = new Date(`${bookingDate}T12:00:00`);
            bookingDayCheck.setHours(0, 0, 0, 0);
            if (bookingDayCheck > maxEquipmentBookableDate) {
                alert('Equipment cannot be booked more than 12 weeks in advance.');
                setSubmitting(false);
                return;
            }
            
            // Validate quantity against available count
            if (selectedEquipmentData && quantity > selectedEquipmentData.available_count) {
                alert(`Cannot book ${quantity} units. Only ${selectedEquipmentData.available_count} available.`);
                setSubmitting(false);
                return;
            }

            if (!acceptedEquipmentLoanPolicy) {
                alert('Please read the Equipment Loan Policy & Terms of Agreement and confirm your acceptance before submitting.');
                setSubmitting(false);
                return;
            }
            
            const response = await fetch('/api/equipment-bookings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    equipment_name: selectedEquipmentData?.equipment_name,
                    equipment_category: selectedEquipmentData?.equipment_category,
                    equipment_type: selectedEquipmentData?.equipment_type,
                    quantity: quantity,
                    eqm_booking_date: bookingDate,
                    eqm_booking_time: bookingTime,
                    eqm_booking_duration: bookingDuration,
                    affiliation: affiliation,
                    remarks: remarks,
                    phone_number: `${countryCode} ${phoneNumber}`,
                    course_code: courseCode,
                    account_code: accountCode,
                    booking_nature: bookingNature,
                    accepted_equipment_loan_policy: true
                })
            });

            if (response.ok) {
                const bookingData = await response.json();
                alert(`Successfully booked ${quantity} unit(s) of ${selectedEquipmentData?.equipment_name}!`);
                setSelectedEquipment('');
                setSelectedEquipmentData(null);
                setBookingDate(getTodayDate());
                setBookingTime(getCurrentTime());
                setBookingDuration(1);
                setCalendarSlotKeys([]);
                setCalendarSelectionValid(false);
                setQuantity(1);
                setAffiliation('');
                setRemarks('');
                setPhoneNumber('');
                setCourseCode('');
                setAccountCode('');
                setBookingNature('');
                setAcceptedEquipmentLoanPolicy(false);
                fetchUserBookings();
                fetchGroupedEquipment(); // Refresh available counts
            } else {
                if (response.status === 401) {
                    handleAuthExpired();
                    return;
                }
                const error = await response.json();
                alert(`Error: ${error.message}`);
            }
        } catch (error) {
            console.error('Error submitting booking:', error);
            alert('Error submitting booking');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelBooking = async (bookingId) => {
        if (!window.confirm('Are you sure you want to cancel this booking?')) return;

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                handleAuthExpired();
                return;
            }
            const response = await fetch(`/api/equipment-bookings/${bookingId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                alert('Booking cancelled successfully');
                fetchUserBookings();
            } else {
                if (response.status === 401) {
                    handleAuthExpired();
                    return;
                }
                alert('Error cancelling booking');
            }
        } catch (error) {
            console.error('Error cancelling booking:', error);
            alert('Error cancelling booking');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return localDate.toLocaleDateString();
    };

    const formatStatus = (status) => {
        const statusMap = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'completed': 'Completed'
        };
        return statusMap[status] || status;
    };

    const getStatusVariant = (status) => {
        const variants = {
            'pending': 'warning',
            'confirmed': 'success',
            'cancelled': 'error',
            'completed': 'neutral'
        };
        return variants[status] || 'neutral';
    };

    if (loading) {
        return (
            <PageTemplate
                title="Equipment Booking"
                description="Reserve lab equipment for your projects"
                loading={true}
            />
        );
    }

    return (
        <PageTemplate
            title="Equipment Booking"
            description="Reserve lab equipment for your projects"
        >

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:items-start">
                {/* Booking Form */}
                <Card className="min-w-0">
                    <Card.Header>
                        <Card.Title className="text-xl font-serif flex items-center">
                            <FiTool className="mr-2" />
                            New Equipment Booking
                        </Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <form onSubmit={handleSubmitBooking} className="space-y-6">
                            
                            {/* Equipment Selection Label */}
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    Equipment
                                </label>
                                
                                {/* Selection Method Toggle */}
                                <div className="flex gap-2 mb-3">
                                    <Button
                                        type="button"
                                        variant={!showScanner ? "primary" : "neutral"}
                                        size="sm"
                                        className="flex-1 justify-center"
                                        onClick={() => setShowScanner(false)}
                                    >
                                        Select from List
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={showScanner ? "primary" : "neutral"}
                                        size="sm"
                                        className="flex-1 justify-center"
                                        onClick={() => setShowScanner(true)}
                                        icon={FiCamera}
                                    >
                                        Scan QR Code
                                    </Button>
                                </div>

                                {/* Scan Result Message */}
                                {scanMessage && (
                                    <div className={`mb-3 p-3 rounded-lg flex items-center gap-2 ${
                                        scanSuccess 
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800' 
                                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                                    }`}>
                                        {scanSuccess ? <FiCheckCircle className="flex-shrink-0" /> : <FiAlertCircle className="flex-shrink-0" />}
                                        <span className="text-sm font-medium">{scanMessage}</span>
                                    </div>
                                )}

                                {/* QR Scanner Component */}
                                {showScanner && (
                                    <div className="space-y-3">
                                        <QrScannerComponent
                                            active={showScanner}
                                            onScan={handleScan}
                                            onError={handleError}
                                        />
                                        <p className="text-xs text-primary-500 dark:text-gray-400 text-center">
                                            Point your camera at the equipment QR code
                                        </p>
                                    </div>
                                )}
                                
                                        {/* Search and Embedded List */}
                                        {!showScanner && (
                                            <div className="space-y-2">
                                                {/* Search Input */}
                                                <div className="relative">
                                                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400 dark:text-gray-500" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search equipment..."
                                                        value={equipmentSearch}
                                                        onChange={(e) => setEquipmentSearch(e.target.value)}
                                                        className="w-full pl-10 pr-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-t-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white placeholder-primary-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-300"
                                                    />
                                                </div>
                                                
                                                {/* Embedded Scrollable List - Grouped Equipment with Counts */}
                                                <div className="border border-t-0 border-primary-300 dark:border-gray-600 rounded-b-lg bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
                                                    {loading ? (
                                                        <div className="p-4 text-center text-primary-500 dark:text-gray-400">Loading equipment...</div>
                                                    ) : groupedEquipment.length === 0 ? (
                                                        <div className="p-4 text-center text-amber-600 dark:text-amber-400">
                                                            No equipment available. Please refresh or contact admin.
                                                        </div>
                                                    ) : (() => {
                                                        const filteredGrouped = groupedEquipment.filter((group) => {
                                                            const q = (equipmentSearch || '').trim().toLowerCase();
                                                            if (!q) return true;
                                                            const name = (group.equipment_name || '').toLowerCase();
                                                            const cat = (group.equipment_category || '').toLowerCase();
                                                            const type = (group.equipment_type || '').toLowerCase();
                                                            const loc = (group.location || '').toLowerCase();
                                                            return name.includes(q) || cat.includes(q) || type.includes(q) || loc.includes(q);
                                                        });
                                                        
                                                        if (filteredGrouped.length === 0) {
                                                            return (
                                                                <div className="p-4 text-center text-primary-500 dark:text-gray-400">
                                                                    No matches found
                                                                </div>
                                                            );
                                                        }
                                                        
                                                        // Group by location
                                                        const byLocation = {};
                                                        filteredGrouped.forEach(group => {
                                                            const loc = group.location || 'Unknown Location';
                                                            if (!byLocation[loc]) {
                                                                byLocation[loc] = [];
                                                            }
                                                            byLocation[loc].push(group);
                                                        });
                                                        
                                                        const locations = Object.keys(byLocation).sort();
                                                        
                                                        return (
                                                            <div>
                                                                {locations.map(location => (
                                                                    <div key={location}>
                                                                        {/* Location Header */}
                                                                        <div className="sticky top-0 z-10 bg-primary-100 dark:bg-gray-700 px-3 py-2 border-b border-primary-200 dark:border-gray-600">
                                                                            <div className="flex items-center gap-2 text-sm font-serif font-semibold text-primary-900 dark:text-white">
                                                                                <FiMapPin className="w-4 h-4" />
                                                                                {location}
                                                                            </div>
                                                                        </div>
                                                                        {/* Grouped Equipment in this Location */}
                                                                        <div className="divide-y divide-primary-100 dark:divide-gray-700">
                                                                            {byLocation[location].map((group) => {
                                                                                const groupKey = `${group.equipment_name}-${group.equipment_category}-${group.equipment_type}`;
                                                                                const isSelected = selectedEquipment === groupKey;
                                                                                const availableCount = group.available_count || 0;
                                                                                
                                                                                return (
                                                                                    <div
                                                                                        key={groupKey}
                                                                                        onClick={() => {
                                                                                            setSelectedEquipment(groupKey);
                                                                                            setSelectedEquipmentData(group);
                                                                                        }}
                                                                                        className={`p-3 cursor-pointer transition-colors ${
                                                                                            isSelected 
                                                                                                ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-primary-500' 
                                                                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-transparent'
                                                                                        }`}
                                                                                    >
                                                                                        <div className="flex justify-between items-center">
                                                                                            <div className="flex-1">
                                                                                                <div className="font-medium text-primary-900 dark:text-white">
                                                                                                    {group.equipment_name}
                                                                                                </div>
                                                                                                <div className="text-xs text-primary-500 dark:text-gray-400">
                                                                                                    {group.equipment_category} - {group.equipment_type}
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="flex items-center gap-2">
                                                                                                <Badge variant="success" size="sm" className="text-xs">
                                                                                                    {availableCount} available
                                                                                                </Badge>
                                                                                                {isSelected && (
                                                                                                    <FiCheckCircle className="text-primary-600 dark:text-primary-400" />
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        )}

                                {/* Currently Selected Equipment Display */}
                                {selectedEquipment && selectedEquipmentData && (
                                    <div className="mt-3 p-3 bg-primary-50 dark:bg-gray-700/50 rounded-lg border border-primary-200 dark:border-gray-600">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-primary-500 dark:text-gray-400 mb-1">Selected Equipment</p>
                                                <p className="font-serif font-medium text-primary-900 dark:text-white">
                                                    {selectedEquipmentData.equipment_name}
                                                </p>
                                                <p className="text-xs text-primary-600 dark:text-gray-400 mt-0.5">
                                                    {selectedEquipmentData.equipment_category} - {selectedEquipmentData.equipment_type}
                                                </p>
                                                <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-medium">
                                                    {selectedEquipmentData.available_count} units available
                                                </p>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedEquipment('');
                                                    setSelectedEquipmentData(null);
                                                }}
                                                icon={FiX}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* The rest of the form (Date, Time, Duration) */}

                            {/* Weekend Error Display */}
                            {error && (
                                <Alert type="warning" className="mb-4">
                                    {error}
                                </Alert>
                            )}

                            {selectedEquipmentData && (
                                <div className="mb-4 p-3 rounded-lg bg-primary-50/80 dark:bg-gray-700/50 border border-primary-200 dark:border-gray-600">
                                    <p className="text-xs font-serif font-medium text-primary-600 dark:text-gray-400 mb-1">
                                        Selected schedule (from calendar)
                                    </p>
                                    <p className="text-sm font-literary text-primary-900 dark:text-white">
                                        {calendarSelectionValid
                                            ? `${bookingDate} starting ${bookingTime} · ${bookingDuration} hour(s)`
                                            : 'Use the Availability Calendar to choose consecutive green hour slots.'}
                                    </p>
                                </div>
                            )}

                            <Input
                                label="Duration (hours)"
                                type="number"
                                min="1"
                                max="24"
                                value={bookingDuration}
                                onChange={(e) => setBookingDuration(parseInt(e.target.value, 10) || 1)}
                                required
                                disabled={calendarSelectionValid && calendarSlotKeys.length > 0}
                                helperText={
                                    calendarSelectionValid && calendarSlotKeys.length > 0
                                        ? 'Updates when you change your calendar selection'
                                        : 'Maximum 24 hours per booking'
                                }
                            />
                            
                            {/* Quantity */}
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    How many would you like to book? *
                                </label>
                                <select
                                    value={quantity}
                                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                                    className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                                    required
                                    disabled={!selectedEquipmentData}
                                >
                                    {selectedEquipmentData ? (
                                        Array.from({ length: Math.min(selectedEquipmentData.available_count, 10) }, (_, i) => i + 1).map(num => (
                                            <option key={num} value={num}>{num}</option>
                                        ))
                                    ) : (
                                        <option value={1}>1</option>
                                    )}
                                </select>
                                {selectedEquipmentData && (
                                    <p className="text-xs text-primary-500 dark:text-gray-400 mt-1">
                                        Maximum available: {selectedEquipmentData.available_count} units
                                    </p>
                                )}
                            </div>
                            
                            {/* Affiliation */}
                            <Input
                                label="Affiliation(s)"
                                value={affiliation}
                                onChange={(e) => setAffiliation(e.target.value)}
                                required
                                placeholder="Your department or organization"
                            />
                            
                            {/* Remarks */}
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    Reason for usage / Remarks *
                                </label>
                                <textarea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    required
                                    placeholder="Please describe your intended use"
                                    className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20 resize-none"
                                    rows="4"
                                />
                            </div>
                            
                            {/* Phone Number */}
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    Phone Number *
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        value={countryCode}
                                        onChange={(e) => setCountryCode(e.target.value)}
                                        className="w-32 px-3 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                                    >
                                        <option value="+852">🇭🇰 +852</option>
                                        <option value="+86">🇨🇳 +86</option>
                                        <option value="+886">🇹🇼 +886</option>
                                        <option value="+1">🇺🇸 +1</option>
                                        <option value="+44">🇬🇧 +44</option>
                                    </select>
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        required
                                        placeholder="Phone number"
                                        className="flex-1 px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                                    />
                                </div>
                            </div>
                            
                            {/* Course Code / Project Title */}
                            <Input
                                label="Please provide the course code/ project title"
                                value={courseCode}
                                onChange={(e) => setCourseCode(e.target.value)}
                                required
                                placeholder="e.g., ARTS1234, Research Project Name"
                            />
                            
                            {/* Account Code (Optional for Students) */}
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    Event funding source (Optional for Student User)
                                </label>
                                <Input
                                    value={accountCode}
                                    onChange={(e) => setAccountCode(e.target.value)}
                                    placeholder="0000000000(9 digits).0000000(6 digits).00000 (5 digits).000.00"
                                    helperText="Please provide account code: 9 digits, 6 digits, 5 digits, 3 digits, 2 digits"
                                />
                            </div>
                            
                            {/* Nature of Booking */}
                            <div>
                                <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                    Nature of the booking *
                                </label>
                                <select
                                    value={bookingNature}
                                    onChange={(e) => setBookingNature(e.target.value)}
                                    className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                                    required
                                >
                                    <option value="">Please choose</option>
                                    <option value="Research">Research</option>
                                    <option value="Events">Events/ Conferences/ Seminars</option>
                                    <option value="Teaching">Teaching and Learning (please provide the course code)</option>
                                </select>
                            </div>

                            <div className="rounded-lg border border-primary-200 dark:border-gray-600 bg-primary-50/80 dark:bg-gray-800/50 p-4 space-y-3">
                                <p className="text-sm font-serif font-medium text-primary-900 dark:text-white">
                                    Equipment Loan Policy &amp; Terms of Agreement
                                </p>
                                <p className="text-sm font-literary text-primary-700 dark:text-gray-300 leading-relaxed">
                                    Before you submit, please read the{' '}
                                    <button
                                        type="button"
                                        onClick={() => setShowPolicyModal(true)}
                                        className="text-primary-600 dark:text-primary-400 underline font-medium font-literary bg-transparent border-0 p-0 cursor-pointer text-left inline"
                                    >
                                        Equipment Loan Policy &amp; Terms of Agreement (ATL &amp; Faculty of Arts)
                                    </button>
                                    {' '}
                                    in full (opens in a window on this page so your form is not cleared).
                                </p>
                                <label className="flex items-start gap-3 cursor-pointer text-sm font-literary text-primary-800 dark:text-gray-200">
                                    <input
                                        type="checkbox"
                                        className="mt-1 h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                                        checked={acceptedEquipmentLoanPolicy}
                                        onChange={(e) => setAcceptedEquipmentLoanPolicy(e.target.checked)}
                                    />
                                    <span>
                                        I have read and agree to the Equipment Loan Policy &amp; Terms of Agreement.
                                    </span>
                                </label>
                            </div>

                            <Button
                                type="submit"
                                variant="primary"
                                className="w-full"
                                loading={submitting}
                                // Ensure booking cannot be submitted if no equipment is selected
                                disabled={
                                    submitting ||
                                    !selectedEquipment ||
                                    !calendarSelectionValid ||
                                    calendarSlotKeys.length === 0 ||
                                    !acceptedEquipmentLoanPolicy
                                }
                                icon={FiCheck}
                            >
                                {submitting ? 'Submitting...' : 'Submit Booking'}
                            </Button>
                        </form>
                    </Card.Content>
                </Card>

                <Card className="min-w-0">
                    <Card.Header>
                        <Card.Title className="text-xl font-serif flex items-center">
                            <FiCalendar className="mr-2" />
                            Availability Calendar
                        </Card.Title>
                    </Card.Header>
                    <Card.Content className="min-w-0 overflow-hidden">
                        {selectedEquipmentData ? (
                            <TimeSlotCalendar
                                itemId={selectedEquipmentData.available_ids?.[0]}
                                equipmentIdsForAvailability={
                                    selectedEquipmentData.available_ids?.map((id) => String(id)) ?? []
                                }
                                resourceName={selectedEquipmentData.equipment_name}
                                resourceType="equipment"
                                totalUnits={selectedEquipmentData.available_count || 1}
                                selectedSlotKeys={calendarSlotKeys}
                                onSelectedSlotKeysChange={handleCalendarSlotsChange}
                                maxBookableDate={maxEquipmentBookableDate}
                                multiSelect
                                isAdminMode={false}
                            />
                        ) : (
                            <p className="text-sm font-literary text-primary-500 dark:text-gray-400 text-center py-8">
                                Select equipment to view the weekly availability grid.
                            </p>
                        )}
                    </Card.Content>
                </Card>

                {/* My Bookings */}
                <Card className="min-w-0">
                    <Card.Header>
                        <Card.Title className="text-xl font-serif flex items-center">
                            <FiTool className="mr-2" />
                            My Equipment Bookings
                        </Card.Title>
                    </Card.Header>
                    <Card.Content>
                        {bookings.length === 0 ? (
                            <div className="text-center py-8">
                                <FiAlertCircle className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
                                <p className="font-literary text-primary-500 dark:text-gray-400">No bookings found</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {bookings.map((booking) => (
                                    <Card key={booking._id} className="border-primary-200 dark:border-gray-700">
                                        <Card.Content className="p-4">
                                            <div className="flex justify-between items-start">
                                                <div className="space-y-2">
                                                    <h3 className="font-serif font-medium text-primary-900 dark:text-white">
                                                        {booking.equipment?.eqm_name || booking.equipment_id?.eqm_name || 'Equipment Name Not Available'}
                                                    </h3>
                                                    <div className="text-sm font-sans text-primary-600 dark:text-gray-400 space-y-1">
                                                        <p><FiCalendar className="inline w-4 h-4 mr-2" />{formatDate(booking.eqm_booking_date)} at {booking.eqm_booking_time}</p>
                                                        <p><FiClock className="inline w-4 h-4 mr-2" />Duration: {booking.eqm_booking_duration} hour(s)</p>
                                                    </div>
                                                    <Badge variant={getStatusVariant(booking.status)}>
                                                        {formatStatus(booking.status)}
                                                    </Badge>
                                                </div>
                                                {booking.status === 'pending' && (
                                                    <Button
                                                        onClick={() => handleCancelBooking(booking._id)}
                                                        variant="danger"
                                                        size="sm"
                                                        icon={FiX}
                                                    >
                                                        Cancel
                                                    </Button>
                                                )}
                                            </div>
                                        </Card.Content>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Card.Content>
                </Card>
            </div>

            <Modal
                isOpen={showPolicyModal}
                onClose={() => setShowPolicyModal(false)}
                title="Equipment Loan Policy & Terms of Agreement"
                size="4xl"
            >
                <p className="text-xs font-literary text-primary-600 dark:text-gray-400 mb-3">
                    Full page (share / bookmark):{' '}
                    <a
                        href="/booking/equipment/policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-primary-700 dark:text-primary-400 break-all"
                    >
                        /booking/equipment/policy
                    </a>
                </p>
                <div className="rounded-lg border-2 border-primary-200 dark:border-gray-600 overflow-hidden shadow-inner bg-white dark:bg-gray-900">
                    <iframe
                        title="Equipment Loan Policy"
                        src="/booking/equipment/policy/embed"
                        className="w-full border-0 block bg-white dark:bg-gray-900"
                        style={{ height: 'min(70vh, 640px)', minHeight: '320px' }}
                        sandbox="allow-same-origin allow-scripts"
                    />
                </div>
                <Modal.Footer className="!mb-0 mt-4">
                    <Button type="button" variant="primary" onClick={() => setShowPolicyModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageTemplate>
    );
}

export default EquipmentBooking;
// import React, { useState, useEffect } from 'react';
// import { Card, Button, Input, Badge, PageTemplate } from '../components/ui';
// import { FiCalendar, FiClock, FiTool, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
// import AvailabilityCalendar from '../components/AvailabilityCalendar';

// function EquipmentBooking() {

//     const [equipment, setEquipment] = useState([]);
//     const [bookings, setBookings] = useState([]);
//     const [selectedEquipment, setSelectedEquipment] = useState('');
//     const [bookingDate, setBookingDate] = useState('');
//     const [bookingTime, setBookingTime] = useState('');
//     const [bookingDuration, setBookingDuration] = useState(1);
//     const [loading, setLoading] = useState(true);
//     const [submitting, setSubmitting] = useState(false);

//     useEffect(() => {
//         fetchEquipment();
//         fetchUserBookings();
//     }, []);

//     const fetchEquipment = async () => {
//         try {
//             setLoading(true);
//             const response = await fetch('/api/equipment', {
//                 headers: {
//                     'Authorization': `Bearer ${localStorage.getItem('token')}`
//                 }
//             });
//             if (response.ok) {
//                 const data = await response.json();
//                 setEquipment(data);
//             }
//         } catch (error) {
//             console.error('Error fetching equipment:', error);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const fetchUserBookings = async () => {
//         try {
//             const response = await fetch('/api/equipment-bookings/user', {
//                 headers: {
//                     'Authorization': `Bearer ${localStorage.getItem('token')}`
//                 }
//             });
//             if (response.ok) {
//                 const data = await response.json();
//                 setBookings(data);
//             }
//         } catch (error) {
//             console.error('Error fetching bookings:', error);
//         }
//     };

//     const handleSubmitBooking = async (e) => {
//         e.preventDefault();
//         setSubmitting(true);

//         try {
//             const response = await fetch('/api/equipment-bookings', {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'Authorization': `Bearer ${localStorage.getItem('token')}`
//                 },
//                 body: JSON.stringify({
//                     equipment_id: selectedEquipment,
//                     eqm_booking_date: bookingDate,
//                     eqm_booking_time: bookingTime,
//                     eqm_booking_duration: bookingDuration
//                 })
//             });

//             if (response.ok) {
//                 alert('Equipment booking submitted successfully!');
//                 setSelectedEquipment('');
//                 setBookingDate('');
//                 setBookingTime('');
//                 setBookingDuration(1);
//                 fetchUserBookings();
//             } else {
//                 const error = await response.json();
//                 alert(`Error: ${error.message}`);
//             }
//         } catch (error) {
//             console.error('Error submitting booking:', error);
//             alert('Error submitting booking');
//         } finally {
//             setSubmitting(false);
//         }
//     };

//     const handleCancelBooking = async (bookingId) => {
//         if (!window.confirm('Are you sure you want to cancel this booking?')) return;

//         try {
//             const response = await fetch(`/api/equipment-bookings/${bookingId}`, {
//                 method: 'DELETE',
//                 headers: {
//                     'Authorization': `Bearer ${localStorage.getItem('token')}`
//                 }
//             });

//             if (response.ok) {
//                 alert('Booking cancelled successfully');
//                 fetchUserBookings();
//             } else {
//                 alert('Error cancelling booking');
//             }
//         } catch (error) {
//             console.error('Error cancelling booking:', error);
//             alert('Error cancelling booking');
//         }
//     };

//     const formatDate = (dateString) => {
//         const date = new Date(dateString);
//         const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
//         return localDate.toLocaleDateString();
//     };

//     const formatStatus = (status) => {
//         const statusMap = {
//             'pending': 'Pending',
//             'confirmed': 'Confirmed',
//             'cancelled': 'Cancelled',
//             'completed': 'Completed'
//         };
//         return statusMap[status] || status;
//     };

//     const getStatusVariant = (status) => {
//         const variants = {
//             'pending': 'warning',
//             'confirmed': 'success',
//             'cancelled': 'error',
//             'completed': 'neutral'
//         };
//         return variants[status] || 'neutral';
//     };

//     if (loading) {
//         return (
//             <PageTemplate
//                 title="Equipment Booking"
//                 description="Reserve lab equipment for your projects"
//                 loading={true}
//             />
//         );
//     }

//     return (
//         <PageTemplate
//             title="Equipment Booking"
//             description="Reserve lab equipment for your projects"
//         >

//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//                 {/* Booking Form */}
//                 <Card>
//                     <Card.Header>
//                         <Card.Title className="text-xl font-serif flex items-center">
//                             <FiTool className="mr-2" />
//                             New Equipment Booking
//                         </Card.Title>
//                     </Card.Header>
//                     <Card.Content>
//                         <form onSubmit={handleSubmitBooking} className="space-y-6">
//                             <div>
//                                 <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
//                                     Equipment
//                                 </label>
//                                 <select
//                                     value={selectedEquipment}
//                                     onChange={(e) => setSelectedEquipment(e.target.value)}
//                                     required
//                                     className="w-full px-4 py-3 font-literary border border-primary-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-primary-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-300"
//                                 >
//                                     <option value="">Select Equipment</option>
//                                     {equipment.map((item) => (
//                                         <option key={item._id} value={item._id}>
//                                             {item.eqm_name} ({item.eqm_cat} - {item.eqm_type})
//                                         </option>
//                                     ))}
//                                 </select>
//                             </div>

//                             <Input
//                                 label="Date"
//                                 type="date"
//                                 value={bookingDate}
//                                 onChange={(e) => setBookingDate(e.target.value)}
//                                 min={new Date().toISOString().split('T')[0]}
//                                 required
//                                 icon={FiCalendar}
//                             />

//                             <Input
//                                 label="Time"
//                                 type="time"
//                                 value={bookingTime}
//                                 onChange={(e) => setBookingTime(e.target.value)}
//                                 required
//                                 icon={FiClock}
//                             />

//                             <Input
//                                 label="Duration (hours)"
//                                 type="number"
//                                 min="1"
//                                 max="24"
//                                 value={bookingDuration}
//                                 onChange={(e) => setBookingDuration(parseInt(e.target.value))}
//                                 required
//                                 helperText="Maximum 24 hours per booking"
//                             />

//                             <Button
//                                 type="submit"
//                                 variant="primary"
//                                 className="w-full"
//                                 loading={submitting}
//                                 disabled={submitting}
//                                 icon={FiCheck}
//                             >
//                                 {submitting ? 'Submitting...' : 'Submit Booking'}
//                             </Button>
//                         </form>
//                     </Card.Content>
//                 </Card>

//                 {/* Calendar */}
//                 <Card>
//                     <Card.Header>
//                         <Card.Title className="text-xl font-serif flex items-center">
//                             <FiCalendar className="mr-2" />
//                             Availability Calendar
//                         </Card.Title>
//                     </Card.Header>
//                     <Card.Content>
//                         <AvailabilityCalendar 
//                             type="equipment"
//                             itemId={selectedEquipment}
//                             onDateSelect={(date) => setBookingDate(date)}
//                         />
//                     </Card.Content>
//                 </Card>

//                 {/* My Bookings */}
//                 <Card>
//                     <Card.Header>
//                         <Card.Title className="text-xl font-serif flex items-center">
//                             <FiTool className="mr-2" />
//                             My Equipment Bookings
//                         </Card.Title>
//                     </Card.Header>
//                     <Card.Content>
//                         {bookings.length === 0 ? (
//                             <div className="text-center py-8">
//                                 <FiAlertCircle className="w-12 h-12 text-primary-300 dark:text-gray-600 mx-auto mb-4" />
//                                 <p className="font-literary text-primary-500 dark:text-gray-400">No bookings found</p>
//                             </div>
//                         ) : (
//                             <div className="space-y-4">
//                                 {bookings.map((booking) => (
//                                     <Card key={booking._id} className="border-primary-200 dark:border-gray-700">
//                                         <Card.Content className="p-4">
//                                             <div className="flex justify-between items-start">
//                                                 <div className="space-y-2">
//                                                     <h3 className="font-serif font-medium text-primary-900 dark:text-white">{booking.equipment?.eqm_name}</h3>
//                                                     <p className="text-sm font-literary text-primary-600 dark:text-gray-400">
//                                                         {booking.equipment?.eqm_cat} - {booking.equipment?.eqm_type}
//                                                     </p>
//                                                     <div className="text-sm font-sans text-primary-600 dark:text-gray-400 space-y-1">
//                                                         <p><FiCalendar className="inline w-4 h-4 mr-2" />{formatDate(booking.eqm_booking_date)} at {booking.eqm_booking_time}</p>
//                                                         <p><FiClock className="inline w-4 h-4 mr-2" />Duration: {booking.eqm_booking_duration} hour(s)</p>
//                                                     </div>
//                                                     <Badge variant={getStatusVariant(booking.status)}>
//                                                         {formatStatus(booking.status)}
//                                                     </Badge>
//                                                 </div>
//                                                 {booking.status === 'pending' && (
//                                                     <Button
//                                                         onClick={() => handleCancelBooking(booking._id)}
//                                                         variant="danger"
//                                                         size="sm"
//                                                         icon={FiX}
//                                                     >
//                                                         Cancel
//                                                     </Button>
//                                                 )}
//                                             </div>
//                                         </Card.Content>
//                                     </Card>
//                                 ))}
//                             </div>
//                         )}
//                     </Card.Content>
//                 </Card>
//             </div>
//         </PageTemplate>
//     );
// }

// export default EquipmentBooking;